import { FileAddOutlined } from '@ant-design/icons'
import { useRateManagement } from '@api/useFreight/useFreight'
import type { Surcharge } from '@api/useFreight/types'
import { GraviButton, Horizontal, Texto, useLocalStorage } from '@gravitate-js/excalibrr'
import { useQueryClient } from '@tanstack/react-query'
import { DatePicker, Form, InputNumber, message, Popover, Switch, Tooltip } from 'antd'
import dayjs from 'dayjs'
import * as React from 'react'
import { useEffect, useState } from 'react'
import type { RateFormValues } from './BaseScheduleRateButton'

export interface SurchargeScheduleButtonProps {
  surcharge: Surcharge
  onSuccess?: () => void
}

export function SurchargeScheduleButton({ surcharge, onSuccess }: SurchargeScheduleButtonProps) {
  const queryClient = useQueryClient()
  const [form] = Form.useForm<RateFormValues>()
  const [popoverVisible, setPopoverVisible] = useState(false)

  // Use localStorage to persist the showEffectiveTo switch state
  const { value: showEffectiveTo, setValue: setShowEffectiveTo } = useLocalStorage('surcharge-show-effective-to', false)

  // When showEffectiveTo changes, we need to update form validation
  useEffect(() => {
    // Reset the effective_to field when the switch is toggled off
    if (!showEffectiveTo) {
      form.setFieldValue('effective_to', undefined)
    }

    // This will force the form to revalidate
    form.validateFields(['effective_to']).catch((e) => {
      // We can ignore validation errors here
    })
  }, [showEffectiveTo, form])

  const { useCreateSurchargeRate } = useRateManagement()
  const createSurchargeRateMutation = useCreateSurchargeRate()

  // Helper function to convert surcharge value for display
  const getDisplayValue = (value: number) => {
    if (surcharge.type === 'Percentage' && value < 1) {
      return value * 100
    }
    return value
  }

  // Calculate minimum allowed date based on rate history
  const calculateMinimumDate = () => {
    // Sort rates by effective_from date in descending order
    const sortedRates =
      surcharge.rates?.sort((a, b) => {
        return dayjs(b.effective_from).valueOf() - dayjs(a.effective_from).valueOf()
      }) || []

    // Get the most recent rate
    const latestRate = sortedRates[0]

    // If latest rate has an end date, minimum is that end date
    if (latestRate?.effective_to) {
      return dayjs(latestRate.effective_to)
    }

    // Otherwise, minimum is day after latest rate's start date
    return latestRate ? dayjs(latestRate.effective_from).add(1, 'day') : dayjs()
  }

  // Determine the default effective date
  const determineDefaultDate = () => {
    const minimumAllowedDate = calculateMinimumDate()
    const today = dayjs()

    // If today is after minimum date, use today
    if (today.isAfter(minimumAllowedDate)) {
      return today
    }

    // Otherwise use minimum date
    return minimumAllowedDate
  }

  const handleScheduleRate = async (values: RateFormValues) => {
    try {
      // Convert from percentage (50) to decimal (0.5) for API if this is a percentage surcharge
      let surchargeValue = values.rate
      if (surcharge.type === 'Percentage') {
        surchargeValue /= 100
      }

      await createSurchargeRateMutation.mutateAsync({
        surcharge_id: surcharge._id,
        effective_date: values.effective_date.format('YYYY-MM-DD'),
        surcharge: surchargeValue,
        effective_to: values.effective_to?.format('YYYY-MM-DD'),
      })

      // Invalidate queries to refresh data
      await queryClient.invalidateQueries({ queryKey: ['surcharges'] })

      setPopoverVisible(false)
      message.success('Rate scheduled successfully')
      if (onSuccess) {
        onSuccess()
      }
    } catch (error) {
      message.error('Failed to schedule rate')
    }
  }

  // Get initial value for the form - if there's a current rate, use that as default
  const currentRate = surcharge.current_surcharge

  const renderPopoverContent = () => {
    const minimumAllowedDate = calculateMinimumDate()
    const defaultScheduleDate = determineDefaultDate()

    return (
      <Form
        form={form}
        layout='vertical'
        onFinish={handleScheduleRate}
        initialValues={{
          effective_date: defaultScheduleDate,
          effective_to: undefined,
          rate: currentRate,
        }}
      >
        <Form.Item
          name='rate'
          label='Surcharge Rate'
          rules={[{ required: true, message: 'Please enter surcharge rate' }]}
          style={{ flex: 1 }}
        >
          <InputNumber
            style={{ width: '100%' }}
            min={0}
            precision={surcharge.type === 'Percentage' ? 3 : 5}
            placeholder='Enter Rate'
            prefix={surcharge.type === 'Per Mile' ? '$' : undefined}
            suffix={surcharge.type === 'Percentage' ? '%' : undefined}
            step={1.0}
            stringMode
          />
        </Form.Item>

        <Form.Item
          name='effective_date'
          label='Effective From'
          rules={[{ required: true, message: 'Please select effective date' }]}
        >
          <DatePicker
            format='MM/DD/YYYY'
            style={{ width: '100%' }}
            disabledDate={(current) => current?.isBefore(minimumAllowedDate, 'day')}
          />
        </Form.Item>

        <Form.Item>
          <Horizontal verticalCenter gap={8}>
            <Switch checked={showEffectiveTo} onChange={setShowEffectiveTo} />
            <Texto>Set end date</Texto>
          </Horizontal>
        </Form.Item>

        {showEffectiveTo && (
          <Form.Item
            name='effective_to'
            label='Effective To'
            rules={[
              {
                required: showEffectiveTo,
                message: 'Please select end date',
              },
              {
                validator: (_, value) => {
                  if (showEffectiveTo && value) {
                    const startDate = form.getFieldValue('effective_date')
                    if (startDate && value.isBefore(startDate)) {
                      return Promise.reject('End date must be after start date')
                    }
                  }
                  return Promise.resolve()
                },
              },
            ]}
          >
            <DatePicker
              format='MM/DD/YYYY'
              style={{ width: '100%' }}
              disabledDate={(current) => {
                const startDate = form.getFieldValue('effective_date')
                return current && (current.isBefore(startDate, 'day') || current.isBefore(minimumAllowedDate, 'day'))
              }}
            />
          </Form.Item>
        )}

        <Horizontal gap={8} justifyContent='flex-end'>
          <GraviButton appearance='outlined' onClick={() => setPopoverVisible(false)}>
            Cancel
          </GraviButton>
          <GraviButton appearance='solid' theme2 htmlType='submit' loading={createSurchargeRateMutation.isPending}>
            Save
          </GraviButton>
        </Horizontal>
      </Form>
    )
  }

  return (
    <Tooltip title='Schedule New Rate'>
      <Popover
        content={renderPopoverContent()}
        title='Schedule New Surcharge Rate'
        trigger='click'
        placement='topRight'
        open={popoverVisible}
        onOpenChange={(visible) => {
          if (visible) {
            const defaultScheduleDate = determineDefaultDate()
            form.setFieldsValue({
              effective_date: defaultScheduleDate,
              effective_to: showEffectiveTo ? dayjs().add(30, 'days') : undefined,
              rate: currentRate,
            })
          }
          setPopoverVisible(visible)
        }}
      >
        <GraviButton
          theme2
          icon={<FileAddOutlined />}
          buttonText='Schedule New Rate'
          onClick={() => setPopoverVisible(true)}
        />
      </Popover>
    </Tooltip>
  )
}
